<template>
  <div class="direct-journey">
    <TabContainer
      v-if="journey?.outbound_booking_tariff_segments?.length"
      class="seat-selection card grid-layout__main"
      :hasTwoContents="hasInboundTrip"
      :tabOneText="$t('booking.outbound')"
      :tabTwoText="$t('booking.inbound')"
      :stickyTabHeader="true"
      v-model="activeTab"
    >
      <template #content1>
        <CarriageLayout
          v-if="canRenderCarriage && carriageLayouts.outbound_0"
          :carriage-layout="carriageLayouts.outbound_0"
          :leg-index="0"
          :current-segment="outboundSegment"
          :from-station="
            journey.outbound_booking_tariff_segments[0]
              .booking_journey_segments[0].departure_station._u_i_c_station_code
          "
          :to-station="
            journey.outbound_booking_tariff_segments[0]
              .booking_journey_segments[0].arrival_station._u_i_c_station_code
          "
          :service-identifier="
            journey.outbound_booking_tariff_segments[0].service_identifier
          "
          :travel-date="journey.outbound_booking_tariff_segments[0].travel_date"
          :booking-number="journey.booking_number"
          :passenger-info="passengerInfo"
          :selected-seats="outboundSelectedSeats"
          :seat-prices="seatPrices.outbound_0 || {}"
          :service-name="
            journey.outbound_booking_tariff_segments[0]
              ?.booking_journey_segments?.[0]?.service_name
          "
          @seat-selected="handleSeatSelected"
        >
          <template
            #footer
            v-if="seatPrices.outbound_0"
          >
            <p>
              {{ $t('travelPlanner.seat.carriageFloors.priceChange') }}
              {{ formatPrice(Object.values(seatPrices.outbound_0)[0] || 0) }} ,-
            </p>
          </template>
        </CarriageLayout>
        <div v-else-if="isLoadingLayout || isLoadingPrices">
          <p>Loading carriage layout...</p>
        </div>
      </template>
      <template #content2>
        <CarriageLayout
          v-if="
            canRenderReturnCarriage &&
            carriageLayouts.inbound_0 &&
            journey?.inbound_booking_tariff_segments?.[0]
          "
          :carriage-layout="carriageLayouts.inbound_0"
          :leg-index="0"
          :current-segment="inboundSegment"
          :from-station="
            journey.inbound_booking_tariff_segments[0]
              .booking_journey_segments[0].departure_station._u_i_c_station_code
          "
          :to-station="
            journey.inbound_booking_tariff_segments[0]
              .booking_journey_segments[0].arrival_station._u_i_c_station_code
          "
          :service-identifier="
            journey.inbound_booking_tariff_segments[0].service_identifier
          "
          :travel-date="journey.inbound_booking_tariff_segments[0].travel_date"
          :booking-number="journey.booking_number"
          :passenger-info="passengerInfo"
          :selected-seats="inboundSelectedSeats"
          :seat-prices="seatPrices.inbound_0 || {}"
          :service-name="
            journey.inbound_booking_tariff_segments[0]
              ?.booking_journey_segments?.[0]?.service_name
          "
          @seat-selected="handleSeatSelected"
        >
          <template
            #footer
            v-if="seatPrices.inbound_0"
          >
            <p>
              {{ $t('travelPlanner.seat.carriageFloors.priceChange') }}
              {{ formatPrice(Object.values(seatPrices.inbound_0)[0] || 0) }} ,-
            </p>
          </template>
        </CarriageLayout>
        <div v-else-if="isLoadingLayout || isLoadingPrices">
          <p>Loading carriage layout...</p>
        </div>
      </template>
    </TabContainer>
  </div>
</template>

<script lang="ts">
  import { defineComponent, computed, ref, onMounted } from 'vue'
  import type { Journey, BookingTariffSegment } from '@/types/journeyTypes'
  import { CarriageSeat, SelectedSeat } from '@/types/seatTypes'
  import TabContainer from '@/components/BaseComponents/TabContainer/TabContainer.vue'
  import CarriageLayout from './CarriageLayout.vue'
  import Button from '@/components/BaseComponents/Button.vue'
  import { useSeatReservationStore } from '@/stores/seatReservationStore'
  import { PropType } from 'vue'
  import {
    searchProducts,
    fetchCarriageLayout,
  } from '@/services/journeyService'
  import { useRouter } from 'vue-router'
  import { useJourneyStore } from '@/stores/journeyStore'
  import { useBookingStore } from '@/stores/bookingStore'
  import { useErrorStore } from '@/stores/errorStore'

  export default defineComponent({
    name: 'DirectJourney',

    components: {
      TabContainer,
      CarriageLayout,
      Button,
    },

    props: {
      journey: {
        type: Object as PropType<Journey>,
        required: true,
      },
      hasInboundTrip: {
        type: Boolean,
        required: true,
      },
      passengerInfo: {
        type: Array as PropType<any[]>,
        required: true,
      },
    },

    setup(props, { emit }) {
      const seatStore = useSeatReservationStore()
      const journeyStore = useJourneyStore()
      const bookingStore = useBookingStore()
      const errorStore = useErrorStore()
      const router = useRouter()
      const seatPrices = ref<Record<string, any>>({})
      const carriageLayouts = ref<Record<string, any>>({})
      const loadingStates = ref({
        outbound: { layout: false, prices: false },
        inbound: { layout: false, prices: false },
      })
      const activeTab = ref<number>(1) 
      const isLoading = ref(false)

      const currentBookingNumber = computed(
        () => bookingStore.booking?.booking_number
      )

      const outboundSelectedSeats = computed(() =>
        seatStore.getSelectedSeatsForLeg('outbound', 0)
      )

      const inboundSelectedSeats = computed(() =>
        seatStore.getSelectedSeatsForLeg('inbound', 0)
      )

      const isLoadingLayout = computed(
        () =>
          loadingStates.value.outbound.layout ||
          loadingStates.value.inbound.layout
      )
      const isLoadingPrices = computed(
        () =>
          loadingStates.value.outbound.prices ||
          loadingStates.value.inbound.prices
      )

      const supportsSeatSelection = (segment: any) => {
        return (
          segment?.required_products?.some(
            (product: any) => 'seat' in product
          ) || false
        )
      }

      // Helper function to check if a segment is cancelled
      // Uses robust cancellation checking to handle boolean, string, and number values
      const isSegmentCancelled = (segment: any): boolean => {
        if (!segment) return false
        
        // Check explicit segment-level cancellation
        let isSegmentExplicitlyCancelled = false
        
        if (typeof segment.cancelled === 'boolean') {
          isSegmentExplicitlyCancelled = segment.cancelled === true
        } else if (typeof segment.cancelled === 'string') {
          isSegmentExplicitlyCancelled = segment.cancelled === 'true' || segment.cancelled === '1'
        } else if (typeof segment.cancelled === 'number') {
          isSegmentExplicitlyCancelled = segment.cancelled === 1
        }
        
        if (isSegmentExplicitlyCancelled) {
          return true
        }
        
        // Check if all required products are cancelled (segment effectively cancelled)
        const requiredProducts = segment.required_products || []
        
        if (requiredProducts.length === 0) {
          return false // No products = not cancelled
        }
        
        const allRequiredProductsCancelled = requiredProducts.every((product: any) => {
          if (!product) return false
          
          let isProductCancelled = false
          
          if (typeof product.cancelled === 'boolean') {
            isProductCancelled = product.cancelled === true
          } else if (typeof product.cancelled === 'string') {
            isProductCancelled = product.cancelled === 'true' || product.cancelled === '1'
          } else if (typeof product.cancelled === 'number') {
            isProductCancelled = product.cancelled === 1
          }
          
          return isProductCancelled
        })
        
        return allRequiredProductsCancelled
      }

      const canRenderCarriage = computed(() => {
        const segment = props.journey?.outbound_booking_tariff_segments?.[0]
        const journeySegment = segment?.booking_journey_segments?.[0]

        return !!(
          segment &&
          journeySegment?.service_name &&
          segment.service_identifier &&
          journeySegment.departure_station?._u_i_c_station_code &&
          journeySegment.arrival_station?._u_i_c_station_code &&
          segment.travel_date &&
          props.journey?.booking_number &&
          Array.isArray(props.passengerInfo) &&
          props.passengerInfo.length > 0 &&
          segment.required_products?.some((product: any) => 'seat' in product)
        )
      })

      const canRenderReturnCarriage = computed(() => {
        const segment = props.journey?.inbound_booking_tariff_segments?.[0]

        return !!(
          props.hasInboundTrip &&
          segment &&
          !loadingStates.value.inbound.layout &&
          !loadingStates.value.inbound.prices &&
          segment.required_products?.some((product: any) => 'seat' in product)
        )
      })

      const loadCarriageLayout = async (leg: any) => {
        const key = `${leg.direction}_${leg.index}`
        if (carriageLayouts.value[key]) {
          return carriageLayouts.value[key]
        }

        try {
          loadingStates.value[leg.direction].layout = true
          const layout = await fetchCarriageLayout(
            leg.service_name,
            leg.departure_station._u_i_c_station_code,
            leg.arrival_station._u_i_c_station_code,
            leg.departure_timestamp
          )

          if (!layout) {

            return null
          }

          if (layout?.carriages) {
            layout.carriages.forEach((carriage: any) => {
              if (carriage.seats) {
                carriage.seats = carriage.seats.map((seat: any) => {
                  let status = 'AVAILABLE'
                  if (seat.allocated || seat.status === 'ALLOCATED') {
                    status = 'ALLOCATED'
                  } else if (seat.blocked || seat.status === 'BLOCKED') {
                    status = 'BLOCKED'
                  }
                  const processedSeat = {
                    ...seat,
                    status,
                    seat_number: seat.seat_number || seat.number,
                  }
                  return processedSeat
                })
              }
            })
          }

          // Set seat properties in the store
          if (layout.seat_properties) {
            seatStore.setSeatProperties(layout.seat_properties)
          }

          carriageLayouts.value[key] = layout
          return layout
        } catch (error) {

          return null
        } finally {
          loadingStates.value[leg.direction].layout = false
        }
      }

      const fetchSeatPrices = async (leg: any) => {
        try {
          loadingStates.value[leg.direction].prices = true
          const prices = await searchProducts(
            leg.service_identifier,
            leg.direction,
            leg.departure_station._u_i_c_station_code,
            leg.arrival_station._u_i_c_station_code,
            leg.departure_timestamp,
            [{ code: 'SF' }]
          )

          // Initialize seat products in the store with direction
          await seatStore.initializeSeatProducts(prices, leg.direction)

          // Create a price map from the API response
          const key = `${leg.direction}_${leg.index}`
          const priceMap: { [key: string]: any } = {}

          prices.data.segment.products.forEach((product: any) => {
            if (
              product.product_type === 'SF' &&
              product.passenger_fares?.[0]?.price
            ) {
              priceMap[product.product_code] = {
                price: {
                  amount: product.passenger_fares[0].price.amount / 100, // Convert from øre to kroner
                  currencyCode: product.passenger_fares[0].price.currency_code,
                },
                tariffCode: product.passenger_fares[0].tariff_code,
              }
            }
          })

          // Map the prices to seats
          const layout = carriageLayouts.value[key]
          if (layout?.carriages) {
            layout.carriages.forEach((carriage: any) => {
              if (carriage.seats) {
                carriage.seats = carriage.seats.map((seat: any) => {
                  let seatPrice = { amount: 0, currencyCode: 'NOK' }
                  let tariffCode = ''
                  let description = 'NONE'

                  // Ensure seat has property_codes array
                  if (!seat.property_codes) {
                    seat.property_codes = []
                  }

                  // If no property codes, add STANDARD_SEAT by default
                  if (seat.property_codes.length === 0) {
                    seat.property_codes.push('STANDARD_SEAT')
                  }

                  // Find matching price from property codes
                  for (const code of seat.property_codes) {
                    const directionProduct = seatStore.getSeatProduct(
                      code,
                      leg.direction
                    )
                    if (directionProduct) {
                      seatPrice = directionProduct.price
                      tariffCode = directionProduct.tariffCode
                      description = directionProduct.description || description
                      break // Use first matching price
                    }
                  }

                  return {
                    ...seat,
                    price: seatPrice,
                    tariffCode,
                    description,
                  }
                })
              }
            })
          }

          seatPrices.value[key] = priceMap
          return prices
        } catch (error) {

          throw error
        } finally {
          loadingStates.value[leg.direction].prices = false
        }
      }

      const handleSeatSelected = (event: {
        seat: CarriageSeat
        legIndex: number
        direction: 'outbound' | 'inbound'
      }) => {
        const { seat, legIndex, direction } = event
        const segment = props.journey?.[
          `${direction}_booking_tariff_segments`
        ]?.[legIndex] as BookingTariffSegment

        seatStore.handleSeatSelection({
          seat,
          segment,
          direction,
          legIndex,
        })
      }

      // Helper function to map old segment IDs to new active segment IDs after rebooking
      const mapSegmentIds = (selectedSeats: any[], availableSegments: any[]) => {
        if (selectedSeats.length === 0 || availableSegments.length === 0) {
          return selectedSeats
        }

        // If we have exactly one selected seat and one active segment, map them
        if (selectedSeats.length === 1 && availableSegments.length === 1) {
          const mappedSeat = { ...selectedSeats[0] }
          const activeSegment = availableSegments[0]
          
          console.log('[DEBUG] DirectJourney mapping seat ID:', {
            originalSeatId: mappedSeat.segmentId,
            newSegmentId: activeSegment.id,
            segmentServiceId: activeSegment.service_identifier
          })
          
          mappedSeat.segmentId = activeSegment.id
          return [mappedSeat]
        }

        // For multiple seats/segments, try to match by service properties
        return selectedSeats.map(seat => {
          const matchingSegment = availableSegments.find(segment => {
            // First try direct ID match
            if (segment.id === seat.segmentId) {
              return true
            }
            
            // If no direct match, try to match by service characteristics
            // This handles the case where segment IDs changed after rebooking
            const seatSegment = props.journey?.outbound_booking_tariff_segments?.find(
              s => s.id === seat.segmentId
            )
            
            if (seatSegment && segment.service_identifier) {
              // Match by service identifier pattern (same route, potentially different date/time)
              const seatServiceParts = seatSegment.service_identifier?.split('|') || []
              const segmentServiceParts = segment.service_identifier.split('|') || []
              
              // Match by route code and line (first 3 parts of service identifier)
              return seatServiceParts.slice(0, 3).join('|') === segmentServiceParts.slice(0, 3).join('|')
            }
            
            return false
          })
          
          if (matchingSegment && matchingSegment.id !== seat.segmentId) {
            console.log('[DEBUG] DirectJourney remapping seat ID:', {
              originalSeatId: seat.segmentId,
              newSegmentId: matchingSegment.id
            })
            
            return { ...seat, segmentId: matchingSegment.id }
          }
          
          return seat
        })
      }

      const handleContinue = async () => {
        if (isLoading.value) return
        isLoading.value = true
        
        try {
          const bookingNumber = props.journey?.booking_number

          if (!bookingNumber) {
            throw new Error('No booking number found')
          }

          console.log('[DEBUG] DirectJourney handleContinue - Journey data:', {
            bookingNumber,
            outboundSegments: props.journey?.outbound_booking_tariff_segments?.length || 0,
            inboundSegments: props.journey?.inbound_booking_tariff_segments?.length || 0
          })

          // Process outbound seats
          const outboundSeats = seatStore.getSelectedSeatsForLeg('outbound', 0)
          const outboundSegments = props.journey?.outbound_booking_tariff_segments || []

          console.log('[DEBUG] DirectJourney outbound processing:', {
            selectedSeats: outboundSeats.length,
            availableSegments: outboundSegments.length,
            segmentIds: outboundSegments.map(s => ({ id: s.id, cancelled: s.cancelled }))
          })

          // Map seat IDs to active segment IDs (handles rebooking scenarios)
          const mappedOutboundSeats = mapSegmentIds(outboundSeats, outboundSegments.filter(s => !isSegmentCancelled(s)))

          if (mappedOutboundSeats.length > 0) {
            for (const seat of mappedOutboundSeats) {
              const matchingSegment = outboundSegments.find(s => s.id === seat.segmentId)
              const segmentCancelled = matchingSegment ? isSegmentCancelled(matchingSegment) : false
              
              console.log('[DEBUG] Processing outbound seat:', {
                seatId: seat.segmentId,
                matchingSegmentFound: !!matchingSegment,
                segmentCancelled,
                seatTariffCode: seat.tariffCode
              })
              
              if (matchingSegment && segmentCancelled) {
                console.log('[DEBUG] Skipping seat patch - segment cancelled:', seat.segmentId)
                continue
              }
              
              console.log('[DEBUG] Patching outbound seat:', seat.segmentId)
              const newSeat = {
                carriage: seat.carriage,
                number: seat.seat_number
              }
              
              try {
                await seatStore.patchBookingWithProduct(
                  bookingNumber,
                  seat.tariffCode,
                  'outbound',
                  seat.passenger_id,
                  undefined, // existingProductId - let the method find existing products
                  newSeat,
                  true,  // useSimplifiedPath
                  seat.segmentId
                )
              } catch (patchError) {
                throw patchError
              }
            }
          }

          // Process inbound seats if present
          if (props.hasInboundTrip) {
            const inboundSeats = seatStore.getSelectedSeatsForLeg('inbound', 0)
            const inboundSegments = props.journey?.inbound_booking_tariff_segments || []
            
            // Map inbound seat IDs as well
            const mappedInboundSeats = mapSegmentIds(inboundSeats, inboundSegments.filter(s => !isSegmentCancelled(s)))

            if (mappedInboundSeats.length > 0) {
              for (const seat of mappedInboundSeats) {
                // Find the matching segment using the seat's segmentId
                const matchingSegment = inboundSegments.find(s => s.id === seat.segmentId)
                
                // Skip cancelled segments to prevent "Tariff no price found" errors
                if (matchingSegment && isSegmentCancelled(matchingSegment)) {
                  console.warn('[SEAT_PATCH] Skipping cancelled inbound segment:', {
                    segmentId: matchingSegment.id,
                    serviceIdentifier: matchingSegment.service_identifier
                  })
                  continue // Skip this seat for cancelled segments
                }
                
                const newSeat = {
                  carriage: seat.carriage,
                  number: seat.seat_number
                }
                
                try {
                  await seatStore.patchBookingWithProduct(
                    bookingNumber,
                    seat.tariffCode,
                    'inbound',
                    seat.passenger_id,
                    undefined, // existingProductId - let the method find existing products
                    newSeat,
                    true,  // useSimplifiedPath
                    seat.segmentId
                  )
                } catch (patchError) {
                  throw patchError
                }
              }
            }
          }

          emit('continue')
        } catch (error) {
          errorStore.setError('Kunne ikke oppdatere setevalg')
        } finally {
          isLoading.value = false
        }
      }

      const outboundSegment = computed(() => {
        const segment = props.journey?.outbound_booking_tariff_segments?.[0]
        const journeySegment = segment?.booking_journey_segments?.[0]
        return {
          direction: 'outbound' as const,
          serviceIdentifier: segment?.service_identifier,
          fromStation: journeySegment?.departure_station._u_i_c_station_code,
          toStation: journeySegment?.arrival_station._u_i_c_station_code,
          travelDate: segment?.travel_date,
          serviceName: journeySegment?.service_name,
          segmentId: segment?.id,
        }
      })

      const inboundSegment = computed(() => {
        const segment = props.journey?.inbound_booking_tariff_segments?.[0]
        const journeySegment = segment?.booking_journey_segments?.[0]
        return {
          direction: 'inbound' as const,
          serviceIdentifier: segment?.service_identifier,
          fromStation: journeySegment?.departure_station._u_i_c_station_code,
          toStation: journeySegment?.arrival_station._u_i_c_station_code,
          travelDate: segment?.travel_date,
          serviceName: journeySegment?.service_name,
          segmentId: segment?.id,
        }
      })

      // const getSeatChangePrice = (
      //   legIndex: number,
      //   direction: 'outbound' | 'inbound'
      // ) => {
      //   return seatPrices.value[`${direction}_${legIndex}`] || {}
      // }

      const formatPrice = (price: number, inOre: boolean = false) => {
        const formattedPrice = new Intl.NumberFormat('nb-NO', {
          style: 'currency',
          currency: 'NOK',
          minimumFractionDigits: inOre ? 0 : 2,
          maximumFractionDigits: inOre ? 0 : 2,
        }).format(price / 100)
        return formattedPrice
      }

      onMounted(async () => {
        if (props.journey?.outbound_booking_tariff_segments?.[0]) {
          const segment = props.journey.outbound_booking_tariff_segments[0]
          const journeySegment = segment.booking_journey_segments[0]
          if (supportsSeatSelection(segment)) {
            const outboundLeg = {
              direction: 'outbound',
              index: 0,
              service_name: journeySegment.service_name,
              service_identifier: segment.service_identifier,
              departure_station: journeySegment.departure_station,
              arrival_station: journeySegment.arrival_station,
              departure_timestamp: segment.travel_date,
            }

            try {
              loadingStates.value.outbound.prices = true
              await fetchSeatPrices(outboundLeg)

              loadingStates.value.outbound.layout = true
              await loadCarriageLayout(outboundLeg)
            } catch (error) {

            } finally {
              loadingStates.value.outbound.prices = false
              loadingStates.value.outbound.layout = false
            }
          }
        }

        if (
          props.hasInboundTrip &&
          props.journey?.inbound_booking_tariff_segments?.[0]
        ) {
          const segment = props.journey.inbound_booking_tariff_segments[0]
          const journeySegment = segment.booking_journey_segments[0]
          if (supportsSeatSelection(segment)) {
            const inboundLeg = {
              direction: 'inbound',
              index: 0,
              service_name: journeySegment.service_name,
              service_identifier: segment.service_identifier,
              departure_station: journeySegment.departure_station,
              arrival_station: journeySegment.arrival_station,
              departure_timestamp: segment.travel_date,
            }

            try {
              loadingStates.value.inbound.prices = true
              await fetchSeatPrices(inboundLeg)

              loadingStates.value.inbound.layout = true
              await loadCarriageLayout(inboundLeg)
            } catch (error) {

            } finally {
              loadingStates.value.inbound.prices = false
              loadingStates.value.inbound.layout = false
            }
          }
        }
      })

      return {
        outboundSelectedSeats,
        inboundSelectedSeats,
        canRenderCarriage,
        canRenderReturnCarriage,
        handleSeatSelected,
        handleContinue,
        isLoadingLayout,
        isLoadingPrices,
        carriageLayouts,
        seatPrices,
        formatPrice,
        outboundSegment,
        inboundSegment,
        activeTab,
        isLoading,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .direct-journey {
    display: grid;
    gap: 1rem;
    grid-template-columns: 1fr;

    .navigation-buttons {
      display: flex;
      justify-content: flex-end;
      padding: 1rem 0;
    }
  }
</style>